// ============================================================================
// Chloe みまもり — レスポンシブ・ファミリーダッシュボード（デザイン準拠 SPA）
// ============================================================================
// モバイル: 単一カラム（ホーム=対象リスト → 詳細）。
// デスクトップ(≥980px): 全幅赤ヘッダー + 左サイドバー常設 + メイン（+高齢者は右レール）。
// 内容/ロジック/API 配線は共通。CSS メディアクエリ + body[data-view] で出し分け。
//
// 要バックエンド対応（デモ挙動）は tasks.md X-5 / D-6 参照（本ファイル末尾 NOTE）。
// ----------------------------------------------------------------------------

import { configureAuth, watchAuthState, signOut, signInWithGoogle } from "./auth";
import { initFirebaseMessaging, requestFcmToken, onForegroundMessage } from "./firebase";
import {
  getNotificationSettings, getDeviceProfile, updateNotificationSettings, updateDeviceProfile,
  getGuardianProfile, updateGuardianDisplayName, pairGuardian, updateFcmToken,
  fetchNotificationHistoryPage, fetchGuardianMessagesPage, sendGuardianMessage, acknowledgeNotification,
  type NotificationHistoryItem, type GuardianMessage, type DeviceNotificationSettings, type DeviceProfile,
} from "./pairing";
import { fetchTweenDetections, fetchTweenScreenTime, decideTweenApproval, type TweenDetection, type TweenScreenTimeResponse } from "./tweenApi";

// ---- OEM テーマ ------------------------------------------------------------
type Oem = "docomo" | "au" | "softbank" | "tokio";
const OEMS: { id: Oem; label: string; short: string; color: string }[] = [
  { id: "docomo", label: "A", short: "A", color: "#C8102E" },
  { id: "au", label: "B", short: "B", color: "#EB5505" },
  { id: "softbank", label: "C", short: "C", color: "#5B5F66" },
  { id: "tokio", label: "D", short: "D", color: "#0A2E73" },
];
function loadOem(): Oem { return (localStorage.getItem("family.oem") as Oem) || "docomo"; }
function applyOem(o: Oem): void {
  document.body.setAttribute("data-oem", o);
  localStorage.setItem("family.oem", o);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", OEMS.find((x) => x.id === o)?.color ?? "#C8102E");
}

// ---- 型・定義 --------------------------------------------------------------
type TargetType = "elderly" | "child";
interface Target { deviceId: string; name: string; type: TargetType; settings: DeviceNotificationSettings; }
interface TargetData { detections: TweenDetection[]; screenTime: TweenScreenTimeResponse | null; timeline: TimelineItem[]; }
interface TimelineItem { kind: "notif" | "msg"; ts: number; notif?: NotificationHistoryItem; msg?: GuardianMessage; }

type Metric = "tsunagari" | "payment" | "web" | "time";
const METRICS: { id: Metric; label: string; cats: string[]; color: string }[] = [
  { id: "tsunagari", label: "つながり", cats: ["grooming", "sexual", "darkbaito", "bullying", "pii", "setting_access"], color: "var(--theme)" },
  { id: "payment", label: "課金・決済", cats: ["payment"], color: "var(--child)" },
  { id: "web", label: "ネット閲覧", cats: ["nsfw", "violence", "domain_category"], color: "var(--sev-low)" },
  { id: "time", label: "利用時間", cats: [], color: "var(--theme)" },
];
const CAT_DESC: Record<string, string> = {
  grooming: "知らない相手からの接触・会おうという誘いを検知しました。", sexual: "画像や動画の要求など、性的なやり取りの兆候を検知しました。",
  darkbaito: "「かんたんに稼げる」等、闇バイトの勧誘を検知しました。", bullying: "きつい言葉・攻撃的なやり取りを検知しました。",
  pii: "住所・学校など個人情報のやり取りを検知しました。", payment: "課金・キャリア決済の試行を検知し、遮断しました。",
  nsfw: "年齢に不適切な画像の表示を検知しました。", violence: "暴力的な画像の表示を検知しました。",
  domain_category: "高リスクサイトへのアクセスを検知しました。", setting_access: "見守り設定を変更しようとした操作を検知しました。",
};

type View = "home" | "elderly" | "child" | "payment" | "deviceSettings" | "account";
let targets: Target[] = [];
const cache: Record<string, TargetData> = {};
let view: View = "home";
let selectedId = "";
let activeMetric: Metric = "tsunagari";
let paymentEvent: TweenDetection | null = null;
let ageStage: "low" | "middle" | "high" = "low";
let guardianName = "";
let loadedDetailFor = ""; // 現在の詳細ビューで読み込み済みの deviceId（人切替で再取得判定）
let fcmToken: string | null = null;

// ---- ユーティリティ ---------------------------------------------------------
function $(id: string): HTMLElement { const e = document.getElementById(id); if (!e) throw new Error(id); return e; }
function esc(s: string): string { const d = document.createElement("div"); d.textContent = s ?? ""; return d.innerHTML; }
function toast(m: string): void { const t = $("toast"); t.textContent = m; t.classList.add("show"); setTimeout(() => t.classList.remove("show"), 1900); }
function toMs(v: string | number | null | undefined): number { if (v == null) return 0; if (typeof v === "number") return v; const t = new Date(v).getTime(); return Number.isFinite(t) ? t : 0; }
function fmtTime(ms: number): string { return ms ? new Date(ms).toLocaleString("ja-JP", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""; }
function fmtMin(min: number): string { const h = Math.floor(min / 60), m = Math.round(min % 60); return h > 0 ? `${h}時間${m}分` : `${m}分`; }
function isDesktop(): boolean { return window.matchMedia("(min-width: 980px)").matches; }
const SEV_COLOR: Record<string, string> = { HIGH: "var(--sev-high)", MEDIUM: "var(--sev-med)", LOW: "var(--sev-low)" };
const SEV_LABEL: Record<string, string> = { HIGH: "HIGH", MEDIUM: "MED", LOW: "LOW" };
function target(id: string): Target | undefined { return targets.find((t) => t.deviceId === id); }
function catBucket(dets: TweenDetection[], m: Metric): TweenDetection[] { const def = METRICS.find((x) => x.id === m)!; return dets.filter((d) => def.cats.includes((d.category || "").toLowerCase())); }
function avatarColor(t: Target): string { return t.type === "child" ? "var(--child)" : "var(--elder-avatar)"; }
// 利用時間のデモ用フォールバック（バックエンド無データ時にデモで空にしないため）。値はデザイン準拠。
const DEMO_SCREENTIME: TweenScreenTimeResponse = {
  date: new Date().toLocaleDateString("ja-JP"), totalMinutes: 187,
  byCategory: { "ゲーム": 82, "SNS": 61, "動画": 33, "ブラウザ": 11 },
  byApp: { "モンスト": 75, "YouTube": 40, "LINE": 38 },
};
function stOrDemo(st: TweenScreenTimeResponse | null): { st: TweenScreenTimeResponse; demo: boolean } {
  return st && st.totalMinutes > 0 && Object.keys(st.byApp ?? {}).length > 0 ? { st, demo: false } : { st: DEMO_SCREENTIME, demo: true };
}
function roleText(t: Target): string { return t.type === "child" ? "お子さま・見守り＋制限" : "高齢者・見守り"; }

// ---- データロード -----------------------------------------------------------
function inferType(name: string): TargetType { return /じい|ばあ|祖父|祖母|おじい|おばあ/.test(name) ? "elderly" : "child"; }

async function loadTargets(): Promise<void> {
  const devices = await getNotificationSettings();
  targets = await Promise.all(devices.map(async (s) => {
    let name = s.deviceName || s.deviceId;
    try { const p = await getDeviceProfile(s.deviceId); if (p.profileName) name = p.profileName; } catch { /* noop */ }
    const savedType = (localStorage.getItem(`family.type.${s.deviceId}`) as TargetType) || inferType(name);
    return { deviceId: s.deviceId, name, type: savedType, settings: s };
  }));
}
async function loadTargetData(t: Target): Promise<TargetData> {
  if (cache[t.deviceId]) return cache[t.deviceId];
  const data: TargetData = { detections: [], screenTime: null, timeline: [] };
  if (t.type === "child") {
    const [d, st] = await Promise.all([
      fetchTweenDetections(t.deviceId, 100).catch(() => ({ detections: [] as TweenDetection[], nextCursor: null })),
      fetchTweenScreenTime(t.deviceId).catch(() => null),
    ]);
    data.detections = d.detections; data.screenTime = st;
  } else { data.timeline = await loadTimeline(t.deviceId); }
  cache[t.deviceId] = data; return data;
}
async function loadTimeline(deviceId: string): Promise<TimelineItem[]> {
  const items: TimelineItem[] = []; let cursor: string | null | undefined = undefined;
  for (let p = 0; p < 5; p++) {
    const page: { items: NotificationHistoryItem[]; nextCursor: string | null } =
      await fetchNotificationHistoryPage(100, cursor).catch(() => ({ items: [] as NotificationHistoryItem[], nextCursor: null as string | null }));
    for (const n of page.items) { if (n.deviceId && n.deviceId !== deviceId) continue; items.push({ kind: "notif", ts: toMs(n.createdAt), notif: n }); }
    cursor = page.nextCursor; if (!cursor || items.length >= 30) break;
  }
  const msg = await fetchGuardianMessagesPage(deviceId, 40).catch(() => ({ items: [] as GuardianMessage[], nextCursor: null }));
  for (const m of msg.items) items.push({ kind: "msg", ts: toMs(m.createdAt), msg: m });
  return items;
}
function latestEmergency(items: TimelineItem[]): NotificationHistoryItem | null {
  return items.filter((i) => i.notif?.category === "emergency").sort((a, b) => b.ts - a.ts)[0]?.notif ?? null;
}

// ---- シェル（topbar / sidebar / mobile header） -----------------------------

function renderTopbar(): void {
  const bar = $("topbar");
  const seg = OEMS.map((o) => `<button class="${o.id === loadOem() ? "on" : ""}" data-oem="${o.id}">${o.short}</button>`).join("");
  bar.innerHTML = `<span class="brand">Chloe みまもり</span><span class="dash">見守りダッシュボード</span>
    <span class="spacer"></span><span style="font-size:12px;opacity:.85;margin-right:4px">カラー</span>
    <span class="oemseg">${seg}</span>
    <span class="acct" id="acctBtn">${esc((guardianName || "?").slice(0, 1))}</span>`;
  bar.querySelectorAll<HTMLElement>("[data-oem]").forEach((b) => b.addEventListener("click", () => { applyOem(b.dataset.oem as Oem); renderTopbar(); renderOemBar(); }));
  $("acctBtn").addEventListener("click", () => nav("account"));
}

function renderSidebar(): void {
  const sb = $("sidebar");
  const today = new Date().toLocaleDateString("ja-JP", { year: "numeric", month: "numeric", day: "numeric", weekday: "short" });
  const cards = targets.map((t) => {
    const d = cache[t.deviceId] ?? { detections: [], screenTime: null, timeline: [] };
    const active = t.deviceId === selectedId ? "active" : "";
    return `<div class="tcard ${active}" data-open="${t.deviceId}">
      <div class="bar" style="background:${t.type === "child" ? "var(--child)" : "var(--theme)"}"></div>
      <div class="head">
        <span class="avatar" style="background:${avatarColor(t)};width:42px;height:42px;font-size:17px">${esc(t.name.slice(0, 1))}</span>
        <div style="flex:1"><div class="name">${esc(t.name)}</div><div class="role">${roleText(t)}</div></div>
        <div class="st">${homeBadge(t, d)}<span class="setlink" data-settings="${t.deviceId}" style="cursor:pointer">設定</span></div>
      </div></div>`;
  }).join("");
  sb.innerHTML = `
    <div class="side-mob-header"><span class="b">Chloe みまもり</span><button class="h-action" data-nav="account" style="background:rgba(255,255,255,.18);color:#fff;border:none;border-radius:9px;padding:7px 12px;font-weight:700;cursor:pointer">設定</button></div>
    <div class="side-home-title" style="padding:16px 18px 4px"><h2 class="page">みまもり</h2><p class="sub" style="margin:2px 0 0">${esc(today)}・${targets.length}人を見守り中</p></div>
    <div class="side-head"><span class="t">見守り対象</span><span class="n">${targets.length}人</span></div>
    <div class="side-list">${cards || '<p class="sub" style="padding:10px">連携中の端末がありません。</p>'}
      <button class="add" data-nav="account">＋ 端末を追加</button></div>
    <div class="side-account" data-nav="account"><span class="av">${esc((guardianName || "?").slice(0, 1))}</span>
      <div><div style="font-weight:700">見守り者の設定</div><div class="r">アカウント・端末追加</div></div><span class="chev">›</span></div>`;
  sb.querySelectorAll<HTMLElement>("[data-open]").forEach((el) => el.addEventListener("click", () => openDetail(el.dataset.open!)));
  sb.querySelectorAll<HTMLElement>("[data-settings]").forEach((el) => el.addEventListener("click", (e) => { e.stopPropagation(); selectedId = el.dataset.settings!; nav("deviceSettings"); }));
  sb.querySelectorAll<HTMLElement>("[data-nav]").forEach((el) => el.addEventListener("click", (e) => { e.stopPropagation(); nav(el.dataset.nav as View); }));
}

function renderHeader(): void {
  const h = $("hdr"); const t = target(selectedId);
  const av = (x: Target) => `<span class="avatar h-avatar" style="background:${avatarColor(x)}">${esc(x.name.slice(0, 1))}</span>`;
  if (view === "deviceSettings" && t) h.innerHTML = `<button class="back" data-back>‹</button><span class="h-title">${av(t)} ${esc(t.name)} の設定</span>`;
  else if (view === "account") h.innerHTML = `<span class="h-title">見守り者の設定</span><button class="h-action solid" data-back>閉じる</button>`;
  else if (view === "payment") h.innerHTML = `<button class="back" data-back>‹</button><span class="h-title">課金・決済の確認</span>`;
  else if (t) h.innerHTML = `<button class="back" data-back>‹</button><span class="h-title">${av(t)} ${esc(t.name)}</span><button class="h-action" data-nav="deviceSettings">設定</button>`;
  else h.innerHTML = `<span class="h-title">Chloe みまもり</span>`;
  h.querySelectorAll<HTMLElement>("[data-nav]").forEach((b) => b.addEventListener("click", () => nav(b.dataset.nav as View)));
  h.querySelectorAll<HTMLElement>("[data-back]").forEach((b) => b.addEventListener("click", goBack));
}

// デスクトップのメイン見出し（モバイルは #hdr が担当）
function mainHead(t: Target, extra = ""): string {
  return `<div class="main-head">
    <span class="avatar" style="background:${avatarColor(t)};width:46px;height:46px;font-size:18px">${esc(t.name.slice(0, 1))}</span>
    <div><div class="name">${esc(t.name)}</div><div class="role">${roleText(t)}${extra}</div></div>
    <div class="act"><button class="h-action" data-nav="deviceSettings">設定</button></div></div>`;
}
function wireMainHead(): void { $("main").querySelectorAll<HTMLElement>(".main-head [data-nav]").forEach((b) => b.addEventListener("click", () => nav(b.dataset.nav as View))); }

function goBack(): void {
  if (view === "payment") { nav("child"); return; }
  if (view === "deviceSettings") { const t = target(selectedId); nav(t?.type === "child" ? "child" : "elderly"); return; }
  nav("home");
}
function openDetail(id: string): void { loadedDetailFor = ""; selectedId = id; const t = target(id); nav(t?.type === "child" ? "child" : "elderly"); } // 対象を開くたび最新取得

// ---- ルーター ---------------------------------------------------------------
async function nav(v: View): Promise<void> {
  // デスクトップでは「ホーム」は最初の対象詳細を表示（サイドバー常設のため）
  if (v === "home" && isDesktop() && targets.length && !selectedId) { selectedId = targets[0].deviceId; }
  if (v === "home" && isDesktop() && targets.length) { const t = target(selectedId) ?? targets[0]; selectedId = t.deviceId; v = t.type === "child" ? "child" : "elderly"; }
  view = v; document.body.dataset.view = v;
  const m = $("main"); m.className = (v === "deviceSettings" || v === "account") ? "panel" : "";
  renderTopbar(); renderSidebar(); renderHeader();
  if (v === "home") { m.innerHTML = targets.length ? "" : `<div class="empty"><span class="ico">🔗</span>連携中の端末がありません。</div>`; }
  else if (v === "elderly") await renderElderly();
  else if (v === "child") await renderChild();
  else if (v === "payment") renderPayment();
  else if (v === "deviceSettings") await renderDeviceSettings();
  else if (v === "account") await renderAccount();
}

// ---- バッジ -----------------------------------------------------------------
function homeBadge(t: Target, d: TargetData): string {
  if (t.type === "elderly") {
    if (latestEmergency(d.timeline)) return `<span class="badge b-emg">緊急</span>`;
    return d.timeline.some((i) => i.notif?.category === "hazard") ? `<span class="badge b-warn">注意</span>` : `<span class="badge b-daily">良好</span>`;
  }
  const flag = d.detections.some((x) => x.severity === "HIGH") || catBucket(d.detections, "payment").length > 0;
  return flag ? `<span class="badge b-warn">要確認</span>` : `<span class="badge b-daily">良好</span>`;
}

// ---- 高齢者詳細 -------------------------------------------------------------
async function renderElderly(force = false): Promise<void> {
  const t = target(selectedId)!;
  if (force || loadedDetailFor !== selectedId) { delete cache[selectedId]; loadedDetailFor = selectedId; } // 人切替/明示更新で最新を取得
  const m = $("main"); const d = await loadTargetData(t);
  renderSidebar();
  const emg = latestEmergency(d.timeline);
  const unread = d.timeline.filter((i) => i.notif?.category === "emergency" && !i.notif?.acknowledgedAt).length;
  const alert = emg
    ? `<div class="alert"><span class="pill">● 緊急</span><h3>緊急対応が必要です</h3><p>${esc(emg.body)}</p><button class="btn btn-primary" data-ack="${esc(emg.id)}">確認する</button></div>`
    : `<div class="card"><div class="empty"><span class="ico">🌿</span>現在、緊急のお知らせはありません。</div></div>`;
  const ordered = [...d.timeline].sort((a, b) => b.ts - a.ts);
  const feed = ordered.length ? ordered.map(timelineRow).join("") : `<div class="empty"><span class="ico">💬</span>まだ通知・伝言はありません。</div>`;
  const status = `<div class="statuscard"><div class="lbl">現在の状態</div><div class="big">${emg ? "緊急対応が必要" : "良好"}</div><div class="sm">${emg ? `未確認の緊急通知が ${unread} 件あります。` : "リスクは低いと判断しています。"}</div></div>`;
  const composer = `<div class="composer"><div style="font-weight:700;margin-bottom:8px">伝言を送る</div>
      <div class="row"><input id="msgInput" type="text" placeholder="伝言を入力…" /><button class="send" id="msgSend">送信</button></div>
      <label class="urgent"><span class="switch"><input type="checkbox" id="msgUrgent"/><span class="sl"></span></span> 急ぎ（クロエが今すぐ伝えます）</label></div>`;
  m.innerHTML = `${mainHead(t, "（クロエの声かけ・緊急連絡・伝言）")}
    <div class="el-grid">
      <div class="el-alert">${alert}</div>
      <div class="el-rail">${status}${composer}</div>
      <div class="el-feed"><div class="section-label">これまでの通知・伝言</div>${feed}</div>
    </div>`;
  wireMainHead();
  $("msgSend").addEventListener("click", () => void sendMsg(t.deviceId));
  ($("msgInput") as HTMLInputElement).addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); void sendMsg(t.deviceId); } });
  m.querySelectorAll<HTMLButtonElement>("[data-ack]").forEach((b) => b.addEventListener("click", () => void ack(b.dataset.ack!, b)));
}
function timelineRow(it: TimelineItem): string {
  if (it.kind === "notif" && it.notif) {
    const n = it.notif;
    const map: Record<string, [string, string, string]> = { emergency: ["var(--sev-high)", "b-emg", "緊急"], hazard: ["var(--sev-med)", "b-warn", "注意"], daily: ["var(--badge-daily-ink)", "b-daily", "日常"] };
    const [dot, badge, bl] = map[n.category] ?? ["var(--muted2)", "b-mute", ""];
    const vid = n.videoUrl ? `<video class="sos-video" src="${esc(n.videoUrl)}" controls playsinline preload="metadata" style="max-width:100%;width:100%;margin-top:6px;border-radius:8px;background:#000"></video>` : "";
    return `<div class="tl-item"><div class="top"><span class="dot" style="background:${dot}"></span>${bl ? `<span class="badge ${badge}">${bl}</span>` : ""}<span class="ttl">${esc(n.title)}</span><span class="time">${fmtTime(it.ts)}</span></div><div class="msg">${esc(n.body)}${vid}</div></div>`;
  }
  const mm = it.msg!; const st = mm.status === "delivered" ? "配信済み" : mm.status === "pending" ? "未配信" : mm.status;
  return `<div class="tl-out"><div class="bubble">${esc(mm.message)}<div class="meta">${st}・${fmtTime(it.ts)}</div></div></div>`;
}
async function sendMsg(deviceId: string): Promise<void> {
  const input = $("msgInput") as HTMLInputElement; const urgent = ($("msgUrgent") as HTMLInputElement).checked; const text = input.value.trim(); if (!text) return;
  input.disabled = true;
  try {
    await sendGuardianMessage(deviceId, text, urgent || undefined);
    cache[deviceId]?.timeline.push({ kind: "msg", ts: Date.now(), msg: { id: `o-${Date.now()}`, deviceId, message: text, status: urgent ? "urgent" : "pending", urgent: urgent || undefined, createdAt: new Date().toISOString(), deliveredAt: null } });
    toast("伝言を送りました"); await renderElderly();
  } catch (e) { input.disabled = false; toast(`送信に失敗: ${(e as Error).message}`); }
}
async function ack(id: string, btn: HTMLButtonElement): Promise<void> {
  btn.disabled = true; btn.textContent = "処理中…";
  try { await acknowledgeNotification(id); btn.textContent = "確認済み"; toast("確認しました"); const it = cache[selectedId]?.timeline.find((x) => x.notif?.id === id); if (it?.notif) it.notif.acknowledgedAt = new Date().toISOString(); }
  catch (e) { btn.disabled = false; btn.textContent = "確認する"; toast(`失敗: ${(e as Error).message}`); }
}

// ---- 子ども詳細 -------------------------------------------------------------
async function renderChild(force = false): Promise<void> {
  const t = target(selectedId)!;
  if (force || loadedDetailFor !== selectedId) { delete cache[selectedId]; loadedDetailFor = selectedId; } // 人切替/タブ切替/明示更新で最新を取得
  const m = $("main"); const d = await loadTargetData(t); renderSidebar();
  const counts: Record<Metric, number> = {
    tsunagari: catBucket(d.detections, "tsunagari").length, payment: catBucket(d.detections, "payment").length,
    web: catBucket(d.detections, "web").length, time: Math.round(stOrDemo(d.screenTime).st.totalMinutes),
  };
  const tiles = METRICS.map((mt) => {
    const on = mt.id === activeMetric;
    const val = mt.id === "time" ? `${counts.time}<small>分</small>` : `${counts[mt.id]}<small>件</small>`;
    return `<div class="metric ${on ? "on" : ""}" data-metric="${mt.id}"><div class="k">${mt.label}</div>
      <div class="v" style="color:${mt.id === "time" ? "var(--theme)" : "var(--ink)"}">${val}</div>
      <div class="ul" style="background:${mt.color};opacity:${mt.id === "web" ? .5 : 1}"></div></div>`;
  }).join("");
  m.innerHTML = `${mainHead(t, "（Chloe Tween 検知・利用時間）")}<div class="metrics">${tiles}</div><div id="msection" style="margin-top:16px"></div>`;
  wireMainHead();
  m.querySelectorAll<HTMLElement>("[data-metric]").forEach((el) => el.addEventListener("click", () => { activeMetric = el.dataset.metric as Metric; void renderChild(true); }));
  renderChildSection(d);
}
function renderChildSection(d: TargetData): void {
  const s = $("msection");
  if (activeMetric === "time") { s.innerHTML = timeSection(d.screenTime); return; }
  const items = catBucket(d.detections, activeMetric); const label = METRICS.find((x) => x.id === activeMetric)!.label;
  if (activeMetric === "payment") {
    // 承認/拒否の対象は「実際に遮断できた決済」= approval_status あり。無いものは通知のみ。
    const pending = items.filter((x) => x.approvalStatus === "blocked_pending");
    s.innerHTML = `<div class="section-label">課金・決済（${items.length}件）・承認待ち ${pending.length}件</div>` + (items.length ? items.map((x, i) => paymentCard(x, i)).join("") : emptySection("💳"));
    s.querySelectorAll<HTMLElement>("[data-pay]").forEach((b) => b.addEventListener("click", async () => { paymentEvent = items[Number(b.dataset.pay)]; ageStage = await computeAgeStage(selectedId); nav("payment"); }));
    return;
  }
  s.innerHTML = `<div class="section-label">${label} の検知（${items.length}件）</div>` + (items.length ? items.map(detCard).join("") : emptySection(activeMetric === "web" ? "🌐" : "🤝"));
}
function emptySection(ico: string): string { return `<div class="card"><div class="empty"><span class="ico">${ico}</span>この分類の検知はまだありません。</div></div>`; }
function detCard(d: TweenDetection): string {
  const cat = (d.category || "").toLowerCase();
  return `<div class="det"><div class="top"><span class="dot" style="width:10px;height:10px;border-radius:50%;background:${SEV_COLOR[d.severity]}"></span>
      <span class="cat">${esc(d.categoryLabel || cat)}</span><span class="chip">${esc(d.source)}</span>
      <span class="badge sev ${d.severity === "HIGH" ? "b-emg" : "b-warn"}">${SEV_LABEL[d.severity] ?? d.severity}</span></div>
    <div class="desc">${esc(CAT_DESC[cat] ?? "")}</div><div class="meta">${esc(d.appPackage)} · ${fmtTime(toMs(d.occurredAt))}</div></div>`;
}
function paymentCard(d: TweenDetection, idx: number): string {
  const st = d.approvalStatus; // 遮断できた決済のみ status あり。無い=画面検知（通知のみ）
  const pending = st === "blocked_pending";
  const badge = pending ? `<span class="badge b-warn">遮断済み・承認待ち</span>`
    : st === "approved" ? `<span class="badge b-daily">承認済み</span>`
      : st === "denied" ? `<span class="badge b-mute">拒否・遮断継続</span>`
        : `<span class="badge b-mute">検知（通知のみ）</span>`;
  const desc = st ? "課金・キャリア決済の試行を検知し、遮断しました。" : "課金の可能性がある画面を検知しました（遮断はしていません）。";
  // 全件「詳細」を開ける。承認/拒否は遮断できたもの(blocked_pending)だけ。
  const action = `<span class="approve-link" data-pay="${idx}">${pending ? "詳細・承認" : "詳細"} ›</span>`;
  return `<div class="${pending ? "det pending" : "det"}"><div class="top"><span class="cat">課金・決済</span><span style="margin-left:auto">${badge}</span></div>
    <div class="desc">${esc(desc)}</div>
    <div class="top" style="margin-top:8px"><span class="meta" style="margin:0">${esc(d.appPackage)} · ${fmtTime(toMs(d.occurredAt))}</span>${action}</div></div>`;
}
// 選択中の子どもの生年月日から年齢段階を推定（未設定/未成年判定不能なら low）。
async function computeAgeStage(deviceId: string): Promise<"low" | "middle" | "high"> {
  try {
    const p = await getDeviceProfile(deviceId);
    if (!p.profileBirthday) return "low";
    const b = new Date(p.profileBirthday); if (Number.isNaN(b.getTime())) return "low";
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth(); if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
    return age <= 12 ? "low" : age <= 15 ? "middle" : "high"; // 小学/中学/高校
  } catch { return "low"; }
}
function timeSection(stIn: TweenScreenTimeResponse | null): string {
  const { st, demo } = stOrDemo(stIn); // 空ならデモデータで表示（デモで空を見せない）
  const total = Math.round(st.totalMinutes);
  const bd = (title: string, map: Record<string, number>, color: string) => {
    const rows = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => {
      const pct = total > 0 ? Math.min(100, Math.round((v / total) * 100)) : 0;
      return `<div class="bd-row"><div class="h"><span>${esc(k)}</span><span class="time">${Math.round(v)}分</span></div><div class="bar-track"><span style="width:${pct}%;background:${color}"></span></div></div>`;
    }).join("");
    return rows ? `<div class="section-label" style="margin:18px 2px 4px">${title}</div>${rows}` : "";
  };
  const demoTag = demo ? ` <span class="badge b-mute" style="font-size:11px;vertical-align:middle">サンプル</span>` : "";
  return `<div class="card"><div style="font-size:16px;font-weight:700">今日の利用時間${demoTag}</div><div class="sub" style="margin:2px 0 0">${esc(st.date)}</div>
    <div class="big-time">${fmtMin(total)}</div>${bd("カテゴリ別", st.byCategory, "var(--theme)")}${bd("アプリ別", st.byApp, "var(--sev-low)")}</div>`;
}

// ---- 課金・決済の承認 -------------------------------------------------------
function renderPayment(): void {
  const m = $("main"); const d = paymentEvent; const t = target(selectedId);
  const actionable = d?.approvalStatus === "blocked_pending"; // 承認/拒否できるのは実遮断のみ
  const seg = (id: "low" | "middle" | "high", label: string) => `<button class="${ageStage === id ? "on" : ""}" data-age="${id}">${label}</button>`;
  const app = d?.appPackage ?? "com.google.android.gm"; const when = d ? fmtTime(toMs(d.occurredAt)) : "";
  // 明細は検知 meta を優先し、無ければデモ値。
  const meta = (d?.meta ?? {}) as Record<string, unknown>;
  const item = typeof meta.item === "string" ? meta.item : "スペシャルパック";
  const amount = typeof meta.amount === "number" ? `¥${meta.amount.toLocaleString()}` : (typeof meta.amount === "string" ? meta.amount : "¥1,200");
  const method = typeof meta.method === "string" ? meta.method : "キャリア決済";
  const metaReason = typeof meta.reason === "string" ? meta.reason : null;

  // 承認フロー（年齢段階・申請理由・確認ステップ・承認/拒否）は実遮断(actionable)時のみ。
  let extra = "", infoText = "", approveLabel = "今回の購入を承認する";
  if (ageStage === "low") infoText = `${esc(t?.name ?? "お子さま")}さんの年齢段階では、保護者が内容を直接確認して承認します。承認するとこの購入のみ許可されます。`;
  else if (ageStage === "middle") { extra = reasonBox(metaReason ?? "限定イベントのアイテムがほしい。"); infoText = "本人からの申請です。保護者が内容を確認し、承認/拒否します。"; }
  else {
    extra = reasonBox(metaReason ?? "部活の遠征費に使いたい。内訳も共有します。") + `<div class="card"><div class="section-label" style="margin:2px 2px 8px">確認ステップ</div><div class="steps">
        <div class="step"><span class="num done">✓</span>本人が申請</div><div class="step"><span class="num done">✓</span>本人が詳細の共有に同意</div>
        <div class="step"><span class="num now">3</span><b>保護者が承認（現在）</b></div></div></div>`;
    infoText = "本人の同意にもとづく確認フローです。本人が詳細の共有に同意済みのため、保護者は最終承認を行います。"; approveLabel = "本人の同意にもとづき承認する";
  }
  const statusBadge = d?.approvalStatus === "approved" ? `<span class="badge b-daily">承認済み</span>`
    : d?.approvalStatus === "denied" ? `<span class="badge b-mute">拒否・遮断継続</span>`
      : d?.approvalStatus === "blocked_pending" ? `<span class="badge b-warn">遮断済み・承認待ち</span>`
        : `<span class="badge b-mute">検知（通知のみ）</span>`;
  const heading = actionable ? "アプリ内購入のリクエスト" : "検知した課金・決済の内容";
  // 年齢段階セレクタ・承認/拒否は actionable のみ。それ以外は状況説明のみ。
  const ageBlock = actionable
    ? `<div class="sub" style="margin:0 0 8px">年齢段階（生年月日から自動判定・デモは手動切替可）</div>
       <div class="seg">${seg("low", "低学年")}${seg("middle", "中学生")}${seg("high", "高校生")}</div>` : "";
  const detail = `<div class="card"><div class="top" style="display:flex;align-items:center">${statusBadge}<span class="time" style="margin-left:auto;color:var(--muted2)">${when}</span></div>
      <h3 style="font-size:20px;font-weight:900;margin:12px 0 8px">${heading}</h3>
      <div class="kv"><span class="k">内容</span><span class="v">${esc(item)}</span></div>
      <div class="kv"><span class="k">金額</span><span class="v" style="color:var(--theme)">${esc(amount)}</span></div>
      <div class="kv"><span class="k">支払い方法</span><span class="v">${esc(method)}</span></div>
      <div class="kv"><span class="k">アプリ</span><span class="v mono">${esc(app)}</span></div>${actionable ? extra : ""}</div>`;
  const actions = actionable
    ? `<div class="infobox"><span class="i">i</span><span>${esc(infoText)}</span></div>
       <button class="btn btn-ok" data-approve>${approveLabel}</button>
       <button class="btn btn-outline-danger" data-deny>拒否する（遮断を継続）</button>`
    : d?.approvalStatus === "approved" ? `<div class="infobox"><span class="i">i</span><span>この決済は承認済みです（この購入のみ許可）。</span></div>`
      : d?.approvalStatus === "denied" ? `<div class="infobox"><span class="i">i</span><span>この決済は拒否済みで、遮断を継続しています。</span></div>`
        : `<div class="infobox"><span class="i">i</span><span>この画面は課金の可能性を検知したものです（遮断はしていません・通知のみ）。承認/拒否の対象外です。</span></div>`;
  // デスクトップ用の戻るボタン付きヘッダー（モバイルは赤ヘッダーの ‹ が担当）。
  const head = `<div class="main-head"><button class="h-action" id="payBack">‹ 戻る</button><div style="margin-left:10px"><div class="name" style="font-size:22px">課金・決済の確認</div></div></div>`;
  m.innerHTML = `${head}${ageBlock}${detail}${actions}`;
  $("main").querySelector<HTMLElement>("#payBack")?.addEventListener("click", () => nav("child"));
  m.querySelectorAll<HTMLElement>("[data-age]").forEach((b) => b.addEventListener("click", () => { ageStage = b.dataset.age as "low" | "middle" | "high"; renderPayment(); }));
  m.querySelector<HTMLElement>("[data-approve]")?.addEventListener("click", () => void decidePayment("approve"));
  m.querySelector<HTMLElement>("[data-deny]")?.addEventListener("click", () => void decidePayment("deny"));
}
async function decidePayment(decision: "approve" | "deny"): Promise<void> {
  const d = paymentEvent; const t = target(selectedId);
  if (!d || !t) { nav("child"); return; }
  try {
    const r = await decideTweenApproval(t.deviceId, d.eventId, d.occurredAt, decision);
    d.approvalStatus = r.approvalStatus; // ローカル反映
    const cd = cache[t.deviceId]?.detections.find((x) => x.eventId === d.eventId);
    if (cd) cd.approvalStatus = r.approvalStatus;
    toast(decision === "approve" ? "承認しました（この購入のみ許可）" : "拒否しました。遮断を継続します");
  } catch (e) { toast(`失敗: ${(e as Error).message}`); }
  nav("child");
}
function reasonBox(text: string): string { return `<div class="reason"><div class="lbl">本人の申請理由</div>${esc(text)}</div>`; }

// ---- 対象別設定 -------------------------------------------------------------
async function renderDeviceSettings(): Promise<void> {
  const m = $("main"); const t = target(selectedId)!; renderSidebar();
  m.innerHTML = `${mainHead(t, " の設定").replace('<div class="act"><button class="h-action" data-nav="deviceSettings">設定</button></div>', "")}
    <div class="sub" style="margin:0 0 10px">この対象者（端末）の設定</div><div id="setbody"><p class="empty">読み込み中…</p></div>`;
  wireMainHead();
  let profile: DeviceProfile = { profileName: t.name, profileGender: "", profileBirthday: "", emergencyPhoneNumber: "" };
  try { profile = await getDeviceProfile(t.deviceId); } catch { /* noop */ }
  const s = t.settings;
  const toggles = t.type === "child"
    ? [["notifyHazard", "つながり（対人リスク）の検知"], ["notifyEmergency", "課金・決済の検知"], ["notifyDaily", "ネット閲覧（不適切コンテンツ）の検知"], ["notifyDaily2", "利用時間（目安超過）の通知"]]
    : [["notifyDaily", "日常の声かけ通知"], ["notifyHazard", "注意通知"], ["notifyEmergency", "緊急通知"]];
  const trow = (field: string, label: string) => {
    const checked = field === "notifyDaily2" ? true : (s as unknown as Record<string, boolean>)[field] ?? true;
    return `<div class="setrow"><span>${label}</span><span class="switch"><input type="checkbox" data-field="${field}" ${checked ? "checked" : ""}/><span class="sl"></span></span></div>`;
  };
  const gopt = (v: string, l: string) => `<option value="${v}" ${profile.profileGender === v ? "selected" : ""}>${l}</option>`;
  const topt = (v: string, l: string) => `<option value="${v}" ${t.type === v ? "selected" : ""}>${l}</option>`;
  $("setbody").innerHTML = `
    <div class="setcard"><h3>通知設定</h3>${toggles.map(([f, l]) => trow(f, l)).join("")}</div>
    <div class="setcard"><h3>プロフィール</h3>
      <div class="fld"><span>名前</span><input id="pfName" value="${esc(profile.profileName)}" maxlength="50"/></div>
      <div class="fld"><span>性別</span><select id="pfGender">${gopt("", "未設定")}${gopt("male", "男性")}${gopt("female", "女性")}${gopt("other", "その他")}</select></div>
      <div class="fld"><span>生年月日</span><input id="pfBirth" type="date" value="${esc(profile.profileBirthday)}"/></div>
      <div class="fld"><span>緊急時の連絡先電話番号</span><input id="pfPhone" type="tel" value="${esc(profile.emergencyPhoneNumber)}" placeholder="電話番号を入力"/></div>
      <button class="btn btn-primary" style="margin:8px 0 14px" id="pfSave">保存する</button></div>
    <div class="setcard"><h3>ファミリー表示</h3><div class="fld"><span>種別</span><select id="typeSel">${topt("child", "お子さま（見守り＋制限）")}${topt("elderly", "ご家族・高齢者（見守り）")}</select></div></div>`;
  $("setbody").querySelectorAll<HTMLInputElement>("[data-field]").forEach((inp) => inp.addEventListener("change", () => void saveToggle(t, inp)));
  $("pfSave").addEventListener("click", () => void saveProfile(t));
  ($("typeSel") as HTMLSelectElement).addEventListener("change", (e) => { t.type = (e.target as HTMLSelectElement).value as TargetType; localStorage.setItem(`family.type.${t.deviceId}`, t.type); delete cache[t.deviceId]; toast("種別を変更しました"); renderSidebar(); });
}
async function saveToggle(t: Target, inp: HTMLInputElement): Promise<void> {
  const field = inp.dataset.field!; const s = t.settings;
  const patch = { notifyHazard: s.notifyHazard, notifyEmergency: s.notifyEmergency, notifyDaily: s.notifyDaily };
  if (field === "notifyHazard") patch.notifyHazard = inp.checked; else if (field === "notifyEmergency") patch.notifyEmergency = inp.checked; else if (field === "notifyDaily") patch.notifyDaily = inp.checked;
  else { toast("設定を保存しました（デモ項目）"); return; }
  try { await updateNotificationSettings(t.deviceId, patch); Object.assign(s, patch); toast("通知設定を保存しました"); }
  catch (e) { inp.checked = !inp.checked; toast(`保存に失敗: ${(e as Error).message}`); }
}
async function saveProfile(t: Target): Promise<void> {
  const prof: DeviceProfile = { profileName: ($("pfName") as HTMLInputElement).value.trim(), profileGender: ($("pfGender") as HTMLSelectElement).value, profileBirthday: ($("pfBirth") as HTMLInputElement).value, emergencyPhoneNumber: ($("pfPhone") as HTMLInputElement).value.trim() };
  try { await updateDeviceProfile(t.deviceId, prof); if (prof.profileName) t.name = prof.profileName; toast("プロフィールを保存しました"); renderSidebar(); }
  catch (e) { toast(`保存に失敗: ${(e as Error).message}`); }
}

// ---- アカウント設定 ---------------------------------------------------------
async function renderAccount(): Promise<void> {
  const m = $("main"); renderSidebar();
  let g = { displayName: guardianName, phoneNumber: "" };
  try { g = await getGuardianProfile(); } catch { /* noop */ }
  guardianName = g.displayName; renderTopbar();
  m.innerHTML = `<div class="main-head"><div class="name" style="font-size:22px">見守り者の設定</div></div>
    <div class="account-user"><span class="av">${esc((g.displayName || "?").slice(0, 1))}</span>
      <div><div style="font-weight:700;font-size:17px">${esc(g.displayName || "ユーザー")}</div><div class="sub" style="margin:2px 0 0">見守り者（あなた）のアカウント</div></div></div>
    <div class="setcard"><h3>見守り者プロフィール</h3>
      <div class="fld"><span>お名前</span><input id="gName" value="${esc(g.displayName)}" maxlength="50"/></div>
      <div class="fld"><span>電話番号</span><input id="gPhone" type="tel" value="${esc(g.phoneNumber)}"/></div>
      <button class="btn btn-primary" style="margin:8px 0 14px" id="gSave">保存する</button></div>
    <div class="setcard"><h3>端末を追加</h3><div class="sub" style="margin:0 2px 10px">連携コードを入力して、新しい見守り対象をペアリングします。</div>
      <div class="composer" style="border:none;padding:0 0 14px"><div class="row"><input id="pairCode" type="text" placeholder="連携コード" style="text-transform:uppercase"/><button class="send" id="pairBtn">連携</button></div></div></div>
    <div class="setcard"><div class="setrow"><div><div style="font-weight:700">ブラウザ通知</div><div class="sub" style="margin:2px 0 0">プッシュ通知を受け取る（FCM）</div></div>
      <button class="h-action" style="background:var(--theme-soft);color:var(--theme-softink);border:none;border-radius:9px;padding:9px 14px;font-weight:700;cursor:pointer" id="notifBtn">${fcmToken ? "有効" : "有効にする"}</button></div></div>
    <button class="btn btn-outline-danger" id="logoutBtn">ログアウト</button>`;
  $("gSave").addEventListener("click", () => void saveGuardian());
  const pc = $("pairCode") as HTMLInputElement; pc.addEventListener("input", () => { const p = pc.selectionStart; pc.value = pc.value.toUpperCase(); pc.setSelectionRange(p, p); });
  $("pairBtn").addEventListener("click", () => void handlePair());
  $("notifBtn").addEventListener("click", () => void enableNotifications($("notifBtn") as HTMLButtonElement));
  $("logoutBtn").addEventListener("click", () => void signOut().catch((e) => toast(`ログアウト失敗: ${(e as Error).message}`)));
}
async function saveGuardian(): Promise<void> {
  const name = ($("gName") as HTMLInputElement).value.trim(); const phone = ($("gPhone") as HTMLInputElement).value.trim();
  if (!name) { toast("名前を入力してください"); return; }
  try { await updateGuardianDisplayName(name, phone || undefined); guardianName = name; toast("見守り者情報を保存しました"); renderTopbar(); renderSidebar(); }
  catch (e) { toast(`保存に失敗: ${(e as Error).message}`); }
}
async function handlePair(): Promise<void> {
  const input = $("pairCode") as HTMLInputElement; const code = input.value.trim().toUpperCase(); if (!code) { toast("連携コードを入力してください"); return; }
  try { const r = await pairGuardian(code); toast(r.message || "端末を連携しました"); input.value = ""; await loadTargets(); renderSidebar(); }
  catch (e) { toast(`連携に失敗: ${(e as Error).message}`); }
}

// ---- OEM バー（モバイル） ---------------------------------------------------
function renderOemBar(): void {
  const bar = $("oembar");
  bar.innerHTML = `<span class="lbl">カラー</span>` + OEMS.map((o) => {
    const on = o.id === loadOem();
    return `<button class="oemchip ${on ? "on" : ""}" data-oem="${o.id}" style="color:${on ? o.color : "#333"}"><span class="dot" style="background:${o.color}"></span>${o.label}</button>`;
  }).join("");
  bar.querySelectorAll<HTMLElement>("[data-oem]").forEach((b) => b.addEventListener("click", () => { applyOem(b.dataset.oem as Oem); renderOemBar(); renderTopbar(); }));
}

// ---- プッシュ通知（FCM）: 旧ホームから移植し index.html を自己完結化 -----------
function isIOS(): boolean { return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); }
function isStandalone(): boolean { return window.matchMedia("(display-mode: standalone)").matches || ("standalone" in navigator && (navigator as unknown as { standalone: boolean }).standalone === true); }
async function registerServiceWorker(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try { await navigator.serviceWorker.register("/firebase-messaging-sw.js"); } catch (e) { console.warn("SW 登録失敗:", e); }
}
async function autoFcmIfGranted(): Promise<void> {
  if (isIOS() && !isStandalone()) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const r = await requestFcmToken();
  if (r.ok) { fcmToken = r.token; try { await updateFcmToken(r.token); } catch { /* noop */ } }
}
async function enableNotifications(btn: HTMLButtonElement): Promise<void> {
  if (isIOS() && !isStandalone()) { toast("iOS はホーム画面に追加してから通知を有効化してください"); return; }
  if (typeof Notification === "undefined") { toast("この環境は通知に対応していません"); return; }
  const prev = btn.textContent; btn.disabled = true; btn.textContent = "設定中…";
  const r = await requestFcmToken();
  if (r.ok) { fcmToken = r.token; try { await updateFcmToken(r.token); } catch { /* noop */ } btn.textContent = "有効"; toast("通知を有効にしました"); return; }
  btn.disabled = false; btn.textContent = prev ?? "有効にする";
  const msg: Record<string, string> = {
    permission_denied: "ブラウザの通知設定で許可してください", permission_default: "許可ダイアログで「許可」を選んでください",
    push_unsupported: "このブラウザは通知に非対応です", messaging_not_initialized: "初期化に失敗しました",
    vapid_missing: "VAPID 未設定（運営へ連絡）", token_empty: "取得に失敗。もう一度お試しください", unknown: "通知の設定に失敗しました",
  };
  toast(msg[r.reason] ?? "通知の設定に失敗しました");
}

// ---- 初期化 -----------------------------------------------------------------
function main(): void {
  configureAuth(); applyOem(loadOem()); renderOemBar();
  void registerServiceWorker().then(() => initFirebaseMessaging()).then(() => { onForegroundMessage((p) => toast(`${p.title}${p.body ? "：" + p.body : ""}`)); });
  let lastDesktop = isDesktop();
  window.addEventListener("resize", () => { const now = isDesktop(); if (now !== lastDesktop) { lastDesktop = now; void nav(view); } });
  watchAuthState(async (user) => {
    if (!user) {
      document.body.dataset.view = "account"; $("topbar").innerHTML = ""; $("sidebar").innerHTML = ""; $("hdr").innerHTML = `<span class="h-title">Chloe みまもり</span>`;
      $("main").innerHTML = `<div class="empty"><span class="ico">🔒</span>ログインが必要です。<br/>
        <button class="btn btn-primary" id="loginBtn" style="max-width:280px;margin:14px auto 0">Google でログイン</button></div>`;
      $("main").querySelector<HTMLButtonElement>("#loginBtn")?.addEventListener("click", () => void signInWithGoogle().catch((e) => toast(`ログインに失敗: ${(e as Error).message}`)));
      return;
    }
    // Cognito Hosted UI からの戻り（?code=... / /auth/callback）を整理
    if (window.location.pathname.startsWith("/auth/callback") || window.location.search.includes("code=")) {
      window.history.replaceState({}, document.title, "/");
    }
    void autoFcmIfGranted(); // 許可済みならトークンを更新（未許可時はアカウント設定から有効化）
    $("status").textContent = "読み込み中…";
    try { await loadTargets(); await Promise.all(targets.map((t) => loadTargetData(t))); try { guardianName = (await getGuardianProfile()).displayName; } catch { /* noop */ } $("status").textContent = ""; await nav("home"); }
    catch (e) { $("status").textContent = `エラー: ${(e as Error).message}`; }
  });
}
main();

// NOTE(backend) 要対応（現状フロントのデモ挙動）:
//  1) 課金の承認/拒否の永続化＋端末ポリシー反映（承認された購入のみ通す）。
//  2) 課金明細（品目/金額/支払い方法/本人申請理由）を検知 meta に含める（現状 meta=hitCount）。
//  3) 年齢段階の生年月日自動判定（現状デモ切替）。
//  4) 利用時間「目安超過」通知トグルを notification-settings に追加（現状デモ項目）。
//  5) device_type を PWA API に公開し種別タグ（localStorage）から移行。
